/**
 * Mobil menu alttan acilan bir sheet oldugunda, tutamac ve baslik alanindan
 * asagi surukleyerek kapatmayi saglar.
 *
 * Element `display: contents` ile isler; kendi kutusu yoktur, sadece
 * cocuklarindan gelen pointer olaylarini dinler. Masaustunde (tutamac gizli,
 * dialog yandan acilir) hicbir sey baglanmaz: her pointerdown'da medya sorgusu
 * kontrol edilir.
 *
 * Surukleme sirasinda `--sheet-drag` degiskeni yazilir; dialog'un acilis/kapanis
 * animasyonu `transform`, surukleme `translate` kullandigi icin ikisi birbirine
 * karismaz.
 */

const MOBILE_QUERY = "(max-width: 767.98px)";
const DRAG_START_THRESHOLD = 4;
const CLOSE_DISTANCE = 90;
const CLOSE_VELOCITY = 0.5;
const UPWARD_RESISTANCE = 0.12;
const SNAP_DURATION = 320;

class MenuDrawerSheet extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {Element | null} */
  #component = null;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {number} */
  #startY = 0;

  /** @type {number} */
  #startTime = 0;

  /** @type {number} */
  #delta = 0;

  /** @type {boolean} */
  #active = false;

  /** @type {number | undefined} */
  #snapTimeout;

  connectedCallback() {
    this.#component = this.closest("dialog-component");
    const dialog = this.#component?.querySelector("dialog");
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.#dialog = dialog;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("pointerdown", this.#onPointerDown, { signal });
    this.#component?.addEventListener("dialog:close", this.#clear, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#detachDragListeners();
    this.#clear();
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === "mouse") return;
    if (this.#pointerId !== null) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    this.#pointerId = event.pointerId;
    this.#startY = event.clientY;
    this.#startTime = event.timeStamp;
    this.#delta = 0;
    this.#active = false;

    window.addEventListener("pointermove", this.#onPointerMove, { passive: false });
    window.addEventListener("pointerup", this.#onPointerUp);
    window.addEventListener("pointercancel", this.#onPointerCancel);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerId !== this.#pointerId || !this.#dialog) return;

    this.#delta = event.clientY - this.#startY;

    if (!this.#active) {
      if (Math.abs(this.#delta) < DRAG_START_THRESHOLD) return;
      this.#active = true;
      this.#dialog.classList.add("is-dragging");
      this.#dialog.classList.remove("is-snapping");
    }

    event.preventDefault();

    const offset = this.#delta > 0 ? this.#delta : this.#delta * UPWARD_RESISTANCE;
    this.#dialog.style.setProperty("--sheet-drag", `${offset.toFixed(1)}px`);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    const elapsed = Math.max(1, event.timeStamp - this.#startTime);
    const velocity = this.#delta / elapsed;
    const wasActive = this.#active;

    this.#detachDragListeners();
    this.#pointerId = null;
    this.#active = false;

    if (!wasActive || !this.#dialog) return;

    this.#dialog.classList.remove("is-dragging");

    const shouldClose =
      this.#delta > CLOSE_DISTANCE || (this.#delta > 24 && velocity > CLOSE_VELOCITY);

    if (shouldClose) {
      const component = /** @type {{ closeDialog?: () => Promise<void> }} */ (this.#component);
      const closing = component?.closeDialog?.();
      if (closing instanceof Promise) {
        closing.then(this.#clear);
      } else {
        this.#clear();
      }
      return;
    }

    this.#snapBack();
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    this.#detachDragListeners();
    this.#pointerId = null;
    this.#active = false;
    this.#dialog?.classList.remove("is-dragging");
    this.#snapBack();
  };

  #detachDragListeners() {
    window.removeEventListener("pointermove", this.#onPointerMove);
    window.removeEventListener("pointerup", this.#onPointerUp);
    window.removeEventListener("pointercancel", this.#onPointerCancel);
  }

  #snapBack() {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.#clear();
      return;
    }

    dialog.classList.add("is-snapping");
    dialog.style.setProperty("--sheet-drag", "0px");

    clearTimeout(this.#snapTimeout);
    this.#snapTimeout = setTimeout(() => {
      dialog.classList.remove("is-snapping");
      dialog.style.removeProperty("--sheet-drag");
    }, SNAP_DURATION);
  }

  #clear = () => {
    clearTimeout(this.#snapTimeout);
    this.#snapTimeout = undefined;
    this.#dialog?.classList.remove("is-dragging", "is-snapping");
    this.#dialog?.style.removeProperty("--sheet-drag");
  };
}

if (!customElements.get("menu-drawer-sheet")) {
  customElements.define("menu-drawer-sheet", MenuDrawerSheet);
}
